import React, { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import api from '../config/api';
import { Grid3X3, ChevronLeft, ChevronRight } from 'lucide-react';
import Masonry from 'react-masonry-css';
import ProfileCard from '../components/ProfileCard';
import { BRAND } from '../config/brand';

const CategoryLanding = () => {
  const breakpointColumns = {
    default: 4,
    1400: 3,
    1100: 3,
    700: 2,
    500: 1
  };
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const pageParam = parseInt(params.get('page') || '1', 10);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  // Resolve category slug -> category UUID/level, then list profiles filtered by correct level.
  const { data, isLoading, error } = useQuery(
    ['category-profiles', slug, page],
    async () => {
      const cat = await api.get(`/api/categories/slug/${encodeURIComponent(slug)}`);
      const category = cat?.data?.category;
      if (!category?.id) return { category: null, profiles: [], pagination: { page, limit: 24, total: 0 } };

      const level = Number(category.level || 0);
      const params = { page, limit: 24 };
      // Backend semantics:
      // - main -> only u.main_category_id matches
      // - sub -> only u.sub_category_id matches
      // - subSub -> only u.sub_sub_category_id matches
      // This is OK for /category landing pages.
      if (level === 1) params.main = category.id;
      else if (level === 2) params.sub = category.id;
      else if (level === 3) params.subSub = category.id;
      else params.main = category.id; // fallback

      // Promo profiles should always be on top in category feeds
      params.promoted_first = 1;

      const res = await api.get('/api/profiles', { params });
      return {
        category,
        profiles: Array.isArray(res?.data?.profiles) ? res.data.profiles : [],
        pagination: res?.data?.pagination || { page, limit: 24, total: 0 }
      };
    },
    { keepPreviousData: true, staleTime: 60 * 1000 }
  );

  const title = data?.category?.name ? String(data.category.name) : 'Категория';
  const description = data?.category?.name
    ? `Профили и творческие коллективы в категории «${String(data.category.name)}».`
    : 'Подборки по выбранной теме.';

  const items = Array.isArray(data?.profiles) ? data.profiles : [];
  const total = Number(data?.pagination?.total) || 0;
  const totalPages = Math.max(1, Math.ceil(total / 24));

  const basePath = `/category/${encodeURIComponent(slug)}`;
  const canonical = useMemo(() => {
    if (page <= 1) return `${BRAND.baseUrl}${basePath}`;
    return `${BRAND.baseUrl}${basePath}?page=${page}`;
  }, [basePath, page]);

  const prevUrl = page > 2 ? `${basePath}?page=${page - 1}` : (page === 2 ? `${basePath}` : null);
  const nextUrl = page < totalPages ? `${basePath}?page=${page + 1}` : null;

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Главная', item: `${BRAND.baseUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Каталог', item: `${BRAND.baseUrl}/katalog` },
      { '@type': 'ListItem', position: 3, name: title, item: canonical }
    ]
  };

  const collectionLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: canonical,
    inLanguage: 'ru-RU',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: items.slice(0, 24).map((p, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `${BRAND.baseUrl}/user/${encodeURIComponent(String(p.username_slug || p.username || p.id))}`,
        name: p.title || p.username || `Профиль ${p.id}`
      }))
    }
  };

  const structuredData = [breadcrumbLd, collectionLd];

  return (
    <>
      <SEO
        title={`${title}`}
        description={description}
        url={`${basePath}${page > 1 ? `?page=${page}` : ''}`}
        type="website"
        canonical={canonical}
        structuredData={structuredData}
      />

      {/* prev/next Links für Crawler */}
      <Helmet>
        {prevUrl && <link rel="prev" href={`${BRAND.baseUrl}${prevUrl}`} />}
        {nextUrl && <link rel="next" href={`${BRAND.baseUrl}${nextUrl}`} />}
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 text-sm text-secondary-600">
            <Link to="/" className="hover:underline">Главная</Link>
            <span>/</span>
            <Link to="/katalog" className="hover:underline">Каталог</Link>
            <span>/</span>
            <span className="text-secondary-900">{title}</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-secondary-900 flex items-center gap-2">
            <Grid3X3 className="w-6 h-6 text-primary-500" />
            <span>{title}</span>
          </h1>
          <p className="mt-2 text-secondary-700">{description}</p>
        </div>

        {/* Status */}
        {isLoading && (
          <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-soft p-6 mb-6">
            <div className="text-secondary-700">Загрузка...</div>
          </div>
        )}
        {error && (
          <div className="rounded-2xl bg-white ring-1 ring-red-200 shadow-soft p-6 mb-6">
            <div className="text-red-700">Ошибка загрузки данных</div>
          </div>
        )}

        {/* Grid Profiles */}
        {!isLoading && !error && (
          <>
            {items.length > 0 ? (
              <div id="profiles-grid">
                <Masonry
                  breakpointCols={breakpointColumns}
                  className="masonry-grid"
                  columnClassName="masonry-grid_column"
                >
                  {items.map((p) => (
                    <div key={p.id} className="mb-6" data-profile-id={p.id}>
                      <ProfileCard profile={p} />
                    </div>
                  ))}
                </Masonry>
              </div>
            ) : (
              <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-soft p-6">
                <div className="text-secondary-700">Нет элементов для отображения.</div>
              </div>
            )}

            {/* Pagination */}
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link
                to={prevUrl || '#'}
                className={`inline-flex items-center px-3 py-2 rounded-lg border ${prevUrl ? 'text-secondary-800 hover:bg-secondary-50 border-secondary-200' : 'text-secondary-400 border-secondary-100 cursor-not-allowed'}`}
                aria-disabled={!prevUrl}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Предыдущая
              </Link>
              <span className="text-sm text-secondary-700">Стр. {page} из {totalPages}</span>
              <Link
                to={nextUrl || '#'}
                className={`inline-flex items-center px-3 py-2 rounded-lg border ${nextUrl ? 'text-secondary-800 hover:bg-secondary-50 border-secondary-200' : 'text-secondary-400 border-secondary-100 cursor-not-allowed'}`}
                aria-disabled={!nextUrl}
              >
                Следующая
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CategoryLanding;
